import React, { useState, Fragment, createElement } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Scan, CheckCircle, XCircle, Search, Users, FileSpreadsheet, Download, BarChart3, ClipboardList, ChevronDown, ChevronUp, Package, Star, Calendar } from 'lucide-react';
import { useStore } from '../lib/store';
type Tab = 'verify' | 'users' | 'reviews' | 'stats';
function downloadCSV(filename: string, csvContent: string) {
  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}
export function AdminPage() {
  const [tab, setTab] = useState<Tab>('stats');
  const [scanResult, setScanResult] = useState('');
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [verifiedUser, setVerifiedUser] = useState<any>(null);
  const [expandedUserPhone, setExpandedUserPhone] = useState<string | null>(null);
  const {
    registeredUsers,
    reservations,
    events,
    reviews,
    redeemReservation
  } = useStore();
  // --- VERIFY ---
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const reservation = reservations.find(r => r.id === scanResult || r.userCode === scanResult.toUpperCase());
    if (reservation) {
      const event = events.find(e => e.id === reservation.eventId);
      setVerifiedUser({
        name: 'User',
        event: event?.title,
        brand: event?.brand,
        status: reservation.status
      });
      setVerificationStatus('success');
      redeemReservation(reservation.id);
    } else {
      setVerificationStatus('error');
    }
  };
  // --- EXPORTS ---
  const exportUsers = () => {
    const headers = 'Name,Phone,Age,City,Occupation,Interests,Pickup Preference,Points,Level,Eligible,Registered At';
    const rows = registeredUsers.map(u => `"${u.name}","${u.phone}","${u.ageRange || ''}","${u.city || ''}","${(u as any).occupation || ''}","${(u.interests || []).join('; ')}","${u.storePreference || ''}",${u.points},"${u.level}",${u.eligible_for_next_event},"${new Date(u.registeredAt).toLocaleString()}"`).join('\n');
    downloadCSV('finns-i-sjon-users.csv', headers + '\n' + rows);
  };
  const exportReservations = () => {
    const headers = 'ID,User,Event,Status,User Code,Staff Code,Created At';
    const rows = reservations.map(r => {
      const event = events.find(e => e.id === r.eventId);
      return `"${r.id}","${r.userId}","${event?.title || ''}","${r.status}","${r.userCode}","${r.staffCode}","${new Date(r.timestamp).toLocaleString()}"`;
    }).join('\n');
    downloadCSV('finns-i-sjon-reservations.csv', headers + '\n' + rows);
  };
  const exportReviews = () => {
    const headers = 'User,Product,Brand,Taste,Packaging,Buy Again,Date';
    const rows = reviews.map(r => `"${r.userId}","${r.productName}","${r.productBrand}",${r.taste === null ? '' : r.taste},${r.packaging === null ? '' : r.packaging},${r.buyAgain === null ? '' : r.buyAgain},"${new Date(r.timestamp).toLocaleString()}"`).join('\n');
    downloadCSV('finns-i-sjon-reviews.csv', headers + '\n' + rows);
  };
  // --- STATS ---
  const totalUsers = registeredUsers.length;
  const totalReservations = reservations.length;
  const totalRedeemed = reservations.filter(r => r.status === 'redeemed' || r.status === 'reviewed').length;
  const totalReviewed = reservations.filter(r => r.status === 'reviewed').length;
  const reviewRate = totalRedeemed > 0 ? Math.round(totalReviewed / totalRedeemed * 100) : 0;
  const eligibleUsers = registeredUsers.filter(u => u.eligible_for_next_event).length;
  const tabs = [{
    id: 'stats',
    label: 'Dashboard',
    icon: BarChart3
  }, {
    id: 'verify',
    label: 'Verify',
    icon: Scan
  }, {
    id: 'users',
    label: 'Users',
    icon: Users
  }, {
    id: 'reviews',
    label: 'Reviews',
    icon: ClipboardList
  }];
  const toggleUserExpanded = (phone: string) => {
    if (expandedUserPhone === phone) {
      setExpandedUserPhone(null);
    } else {
      setExpandedUserPhone(phone);
    }
  };
  return <div className="min-h-screen bg-gray-950 text-white" data-id="element-90">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-91">
        <div className="flex items-center space-x-3" data-id="element-92">
          <h1 className="text-xl font-bold" data-id="element-93">Finns i Sjön</h1>
          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-mono" data-id="element-94">
            ADMIN
          </span>
        </div>
        <div className="bg-green-500 px-3 py-1 rounded-full text-xs font-bold text-black" data-id="element-95">
          Online
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 flex space-x-1 overflow-x-auto" data-id="element-96">
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id as Tab)} className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-300'}`} data-id="element-97">
            <t.icon className="w-4 h-4" data-id="element-98" />
            <span data-id="element-99">{t.label}</span>
          </button>)}
      </div>

      <div className="p-6" data-id="element-100">
        {/* STATS TAB */}
        {tab === 'stats' && <div className="space-y-6" data-id="element-101">
            <div className="grid grid-cols-2 gap-4" data-id="element-102">
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-103">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-104">
                  Total Users
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-105">
                  {totalUsers}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-106">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-107">
                  Reservations
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-108">
                  {totalReservations}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-109">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-110">
                  Redeemed
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-111">
                  {totalRedeemed}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-112">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-113">
                  Review Rate
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-114">
                  {reviewRate}%
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-115">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-116">
                  Eligible for Next
                </p>
                <p className="text-3xl font-black text-green-400 mt-1" data-id="element-117">
                  {eligibleUsers}
                </p>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-id="element-118">
                <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-119">
                  Total Reviews
                </p>
                <p className="text-3xl font-black text-white mt-1" data-id="element-120">
                  {reviews.length}
                </p>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-121">
              <h3 className="font-bold mb-4 flex items-center" data-id="element-122">
                <Download className="w-5 h-5 mr-2 text-blue-400" data-id="element-123" />
                Export Data
              </h3>
              <div className="space-y-3" data-id="element-124">
                <button onClick={exportUsers} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-125">
                  <div className="flex items-center" data-id="element-126">
                    <FileSpreadsheet className="w-5 h-5 text-green-400 mr-3" data-id="element-127" />
                    <div className="text-left" data-id="element-128">
                      <p className="font-medium" data-id="element-129">Users Data</p>
                      <p className="text-xs text-gray-500" data-id="element-130">
                        {totalUsers} records • Name, Phone, Age, City,
                        Interests, Pickup
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded font-mono" data-id="element-131">
                    .CSV
                  </span>
                </button>
                <button onClick={exportReservations} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-132">
                  <div className="flex items-center" data-id="element-133">
                    <FileSpreadsheet className="w-5 h-5 text-blue-400 mr-3" data-id="element-134" />
                    <div className="text-left" data-id="element-135">
                      <p className="font-medium" data-id="element-136">Reservations</p>
                      <p className="text-xs text-gray-500" data-id="element-137">
                        {totalReservations} records • Event, Status, Codes,
                        Timestamps
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded font-mono" data-id="element-138">
                    .CSV
                  </span>
                </button>
                <button onClick={exportReviews} className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 p-4 rounded-lg transition-colors" data-id="element-139">
                  <div className="flex items-center" data-id="element-140">
                    <FileSpreadsheet className="w-5 h-5 text-purple-400 mr-3" data-id="element-141" />
                    <div className="text-left" data-id="element-142">
                      <p className="font-medium" data-id="element-143">Product Reviews</p>
                      <p className="text-xs text-gray-500" data-id="element-144">
                        {reviews.length} records • Product, Taste, Packaging,
                        Buy Again
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded font-mono" data-id="element-145">
                    .CSV
                  </span>
                </button>
              </div>
            </div>
          </div>}

        {/* VERIFY TAB */}
        {tab === 'verify' && <div className="space-y-6" data-id="element-146">
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-147">
              <h2 className="text-lg font-bold mb-4 flex items-center" data-id="element-148">
                <Scan className="w-5 h-5 mr-2 text-blue-400" data-id="element-149" />
                Verify Redemption
              </h2>
              <form onSubmit={handleVerify} className="space-y-4" data-id="element-150">
                <Input placeholder="Enter 4-char Code" value={scanResult} onChange={e => setScanResult(e.target.value)} className="bg-gray-800 border-gray-700 text-white text-center text-xl" autoFocus data-id="element-151" />
                <Button type="submit" className="w-full" data-id="element-152">
                  Verify
                </Button>
              </form>
            </div>

            {verificationStatus === 'success' && verifiedUser && <div className="bg-green-900/30 border border-green-500/50 rounded-2xl p-6" data-id="element-153">
                <div className="flex items-center mb-4" data-id="element-154">
                  <CheckCircle className="w-8 h-8 text-green-400 mr-3" data-id="element-155" />
                  <div data-id="element-156">
                    <h3 className="text-xl font-bold" data-id="element-157">Verified!</h3>
                    <p className="text-green-300 text-sm" data-id="element-158">Valid reservation</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-300" data-id="element-159">
                  <p data-id="element-160">
                    Product:{' '}
                    <span className="text-white font-bold" data-id="element-161">
                      {verifiedUser.brand} Box
                    </span>
                  </p>
                  <p data-id="element-162">Status: {verifiedUser.status}</p>
                </div>
                <Button onClick={() => {
            setVerificationStatus('idle');
            setScanResult('');
            setVerifiedUser(null);
          }} className="w-full mt-6 bg-green-600 hover:bg-green-700 border-none" data-id="element-163">
                  Scan Next
                </Button>
              </div>}

            {verificationStatus === 'error' && <div className="bg-red-900/30 border border-red-500/50 rounded-2xl p-6" data-id="element-164">
                <div className="flex items-center mb-2" data-id="element-165">
                  <XCircle className="w-8 h-8 text-red-400 mr-3" data-id="element-166" />
                  <h3 className="text-xl font-bold" data-id="element-167">Invalid</h3>
                </div>
                <p className="text-red-300 text-sm mb-4" data-id="element-168">
                  No reservation found.
                </p>
                <Button onClick={() => setVerificationStatus('idle')} variant="secondary" className="w-full bg-red-900/50 text-red-200 border-red-800" data-id="element-169">
                  Try Again
                </Button>
              </div>}
          </div>}

        {/* USERS TAB */}
        {tab === 'users' && <div className="space-y-4" data-id="element-170">
            <div className="flex justify-between items-center" data-id="element-171">
              <h2 className="text-lg font-bold" data-id="element-172">
                {totalUsers} Registered Users
              </h2>
              <button onClick={exportUsers} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-173">
                <Download className="w-4 h-4 mr-1" data-id="element-174" /> Export
              </button>
            </div>

            <div className="overflow-x-auto -mx-6" data-id="element-175">
              <table className="w-full text-sm" data-id="element-176">
                <thead data-id="element-177">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800" data-id="element-178">
                    <th className="px-6 py-3" data-id="element-179">User</th>
                    <th className="px-4 py-3" data-id="element-180">Phone</th>
                    <th className="px-4 py-3" data-id="element-181">Catches</th>
                    <th className="px-4 py-3" data-id="element-182">Attendance</th>
                    <th className="px-4 py-3" data-id="element-183">Points</th>
                    <th className="px-4 py-3" data-id="element-184">Status</th>
                    <th className="px-6 py-3" data-id="element-185"></th>
                  </tr>
                </thead>
                <tbody data-id="element-186">
                  {registeredUsers.map((u, i) => {
                const userReservations = reservations.filter(r => r.userId === u.phone);
                const attendedCount = userReservations.filter(r => r.status === 'redeemed' || r.status === 'reviewed').length;
                const isReturning = attendedCount >= 2;
                const isExpanded = expandedUserPhone === u.phone;
                return <Fragment key={i} data-id="element-187">
                        <tr onClick={() => toggleUserExpanded(u.phone)} className={`border-b border-gray-800/50 hover:bg-gray-900/50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-900/50' : ''}`} data-id="element-188">
                          <td className="px-6 py-4" data-id="element-189">
                            <div className="font-medium text-white whitespace-nowrap" data-id="element-190">
                              {u.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-191">
                              {u.city} • {u.ageRange}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-400 font-mono text-xs" data-id="element-192">
                            {u.phone}
                          </td>
                          <td className="px-4 py-4" data-id="element-193">
                            <div className="flex items-center space-x-1" data-id="element-194">
                              <Package className="w-4 h-4 text-blue-400" data-id="element-195" />
                              <span className="font-bold text-white" data-id="element-196">
                                {userReservations.length}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4" data-id="element-197">
                            {isReturning ? <span className="bg-purple-500/20 text-purple-400 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-198">
                                Returning
                              </span> : <span className="text-gray-500 text-xs font-medium" data-id="element-199">
                                {attendedCount} / {events.length}
                              </span>}
                          </td>
                          <td className="px-4 py-4 font-bold text-white" data-id="element-200">
                            {u.points}
                          </td>
                          <td className="px-4 py-4" data-id="element-201">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded ${u.eligible_for_next_event ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`} data-id="element-202">
                              {u.eligible_for_next_event ? 'ELIGIBLE' : 'PENDING'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right" data-id="element-203">
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400 inline-block" data-id="element-204" /> : <ChevronDown className="w-5 h-5 text-gray-400 inline-block" data-id="element-205" />}
                          </td>
                        </tr>

                        {/* Expanded Details Row */}
                        {isExpanded && <tr className="bg-gray-900/30 border-b border-gray-800/50" data-id="element-206">
                            <td colSpan={7} className="px-6 py-6" data-id="element-207">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-208">
                                {/* Profile Info */}
                                <div className="space-y-4" data-id="element-209">
                                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-210">
                                    Profile Details
                                  </h4>
                                  <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 space-y-3" data-id="element-211">
                                    <div data-id="element-212">
                                      <p className="text-xs text-gray-500" data-id="element-213">
                                        Occupation
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-214">
                                        {(u as any).occupation || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-215">
                                      <p className="text-xs text-gray-500" data-id="element-216">
                                        Pickup Preference
                                      </p>
                                      <p className="text-sm text-white font-medium" data-id="element-217">
                                        {u.storePreference || 'Not specified'}
                                      </p>
                                    </div>
                                    <div data-id="element-218">
                                      <p className="text-xs text-gray-500 mb-1" data-id="element-219">
                                        Interests
                                      </p>
                                      <div className="flex flex-wrap gap-1" data-id="element-220">
                                        {(u.interests || []).map(int => <span key={int} className="bg-blue-500/20 text-blue-300 text-[10px] px-1.5 py-0.5 rounded" data-id="element-221">
                                            {int}
                                          </span>)}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Catch History */}
                                <div className="md:col-span-2 space-y-4" data-id="element-222">
                                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-223">
                                    Catch History
                                  </h4>

                                  {userReservations.length === 0 ? <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 text-center text-gray-500 text-sm" data-id="element-224">
                                      No catches recorded yet.
                                    </div> : <div className="space-y-3" data-id="element-225">
                                      {userReservations.map(res => {
                              const event = events.find(e => e.id === res.eventId);
                              const userReviews = reviews.filter(r => r.reservationId === res.id);
                              let statusColor = 'bg-gray-700 text-gray-300';
                              if (res.status === 'redeemed') statusColor = 'bg-yellow-500/20 text-yellow-400';
                              if (res.status === 'reviewed') statusColor = 'bg-green-500/20 text-green-400';
                              return <div key={res.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-226">
                                            <div className="flex items-start space-x-3" data-id="element-227">
                                              <div className="bg-gray-800 p-2 rounded-lg mt-1" data-id="element-228">
                                                <Calendar className="w-4 h-4 text-gray-400" data-id="element-229" />
                                              </div>
                                              <div data-id="element-230">
                                                <h5 className="font-bold text-white text-sm" data-id="element-231">
                                                  {event?.title || 'Unknown Event'}
                                                </h5>
                                                <p className="text-xs text-gray-400" data-id="element-232">
                                                  {event?.brand} • {event?.date}
                                                </p>
                                                <div className="flex items-center mt-2 space-x-2" data-id="element-233">
                                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${statusColor}`} data-id="element-234">
                                                    {res.status}
                                                  </span>
                                                  <span className="text-xs text-gray-500 font-mono" data-id="element-235">
                                                    Code: {res.userCode}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="bg-gray-950 rounded-lg p-3 border border-gray-800 min-w-[140px]" data-id="element-236">
                                              <div className="flex items-center justify-between mb-1" data-id="element-237">
                                                <span className="text-xs text-gray-400" data-id="element-238">
                                                  Reviews
                                                </span>
                                                <span className="text-xs font-bold text-white" data-id="element-239">
                                                  {userReviews.length} items
                                                </span>
                                              </div>
                                              {res.status === 'reviewed' ? <div className="flex items-center text-green-400 text-xs font-medium" data-id="element-240">
                                                  <CheckCircle className="w-3 h-3 mr-1" data-id="element-241" />{' '}
                                                  Completed
                                                </div> : res.status === 'redeemed' ? <div className="flex items-center text-yellow-400 text-xs font-medium" data-id="element-242">
                                                  <Star className="w-3 h-3 mr-1" data-id="element-243" />{' '}
                                                  Pending Review
                                                </div> : <div className="text-gray-500 text-xs italic" data-id="element-244">
                                                  Not collected
                                                </div>}
                                            </div>
                                          </div>;
                            })}
                                    </div>}
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div>}

        {/* REVIEWS TAB */}
        {tab === 'reviews' && <div className="space-y-4" data-id="element-245">
            <div className="flex justify-between items-center" data-id="element-246">
              <h2 className="text-lg font-bold" data-id="element-247">
                {reviews.length} Product Reviews
              </h2>
              <button onClick={exportReviews} className="flex items-center text-sm text-blue-400 hover:text-blue-300" data-id="element-248">
                <Download className="w-4 h-4 mr-1" data-id="element-249" /> Export
              </button>
            </div>

            <div className="overflow-x-auto -mx-6" data-id="element-250">
              <table className="w-full text-sm" data-id="element-251">
                <thead data-id="element-252">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-800" data-id="element-253">
                    <th className="px-6 py-3" data-id="element-254">User</th>
                    <th className="px-4 py-3" data-id="element-255">Product</th>
                    <th className="px-4 py-3" data-id="element-256">Brand</th>
                    <th className="px-4 py-3" data-id="element-257">Taste</th>
                    <th className="px-4 py-3" data-id="element-258">Packaging</th>
                    <th className="px-4 py-3" data-id="element-259">Buy Again</th>
                    <th className="px-4 py-3" data-id="element-260">Date</th>
                  </tr>
                </thead>
                <tbody data-id="element-261">
                  {reviews.map(r => <tr key={r.id} className="border-b border-gray-800/50 hover:bg-gray-900/50" data-id="element-262">
                      <td className="px-6 py-3 text-gray-400 font-mono text-xs" data-id="element-263">
                        {r.userId}
                      </td>
                      <td className="px-4 py-3 font-medium text-white" data-id="element-264">
                        {r.productName}
                      </td>
                      <td className="px-4 py-3 text-gray-400" data-id="element-265">
                        {r.productBrand}
                      </td>
                      <td className="px-4 py-3" data-id="element-266">
                        {r.taste === null ? '—' : r.taste ? <span className="text-green-400" data-id="element-267">👍</span> : <span className="text-red-400" data-id="element-268">👎</span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-269">
                        {r.packaging === null ? '—' : r.packaging ? <span className="text-green-400" data-id="element-270">👍</span> : <span className="text-red-400" data-id="element-271">👎</span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-272">
                        {r.buyAgain === null ? '—' : r.buyAgain ? <span className="text-green-400" data-id="element-273">👍</span> : <span className="text-red-400" data-id="element-274">👎</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs" data-id="element-275">
                        {new Date(r.timestamp).toLocaleDateString()}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}